import React, { useCallback, useMemo } from "react";
import { WSControl } from "../WSControl/WSControl";
import { WSElement } from "../WSElement/WSElement.component";
import { WSText } from "../core/WSText/WSText.component";
import styles from "./WSTable.module.scss";
import { WSTableColumn, WSTableGroupHeaderConfig, WSTableItem } from "./types";

type HeaderProps = {
  header: WSTableGroupHeaderConfig;
  tableData: Array<WSTableItem>;
  selectionType?: "checkbox" | "radio";
  showSelectionColumn?: boolean;
  selection?: Array<WSTableItem>;
  onSelectionChange?: (selection: Array<WSTableItem>) => void;
  columns: Array<WSTableColumn>;
};

export const GroupHeader: React.FC<HeaderProps> = ({
  header,
  tableData = [],
  selectionType = "checkbox",
  showSelectionColumn,
  selection = [],
  onSelectionChange,
  columns
}) => {
  // Get the ids of the selected items that are in the current group
  const groupSelectionIds = useMemo(
    () =>
      selection.reduce<string[]>((acc, item) => {
        const matchesCurrentGroup = !!tableData.find(
          (groupItem) => groupItem.id === item.id
        );

        if (matchesCurrentGroup) {
          return [...acc, item.id];
        }
        return acc;
      }, []),
    [selection, tableData]
  );

  const isAllSelected = useMemo(() => {
    if (
      groupSelectionIds.length > 0 &&
      groupSelectionIds.length < tableData.length
    ) {
      return "indeterminate";
    }

    return groupSelectionIds.length === tableData.length;
  }, [groupSelectionIds.length, tableData.length]);

  const handleSelectAll = useCallback(() => {
    if (isAllSelected === true) {
      onSelectionChange?.(
        selection.filter((item) => !groupSelectionIds.includes(item.id))
      );
    } else {
      const newSelection = selection.filter(
        (item) => !groupSelectionIds.includes(item.id)
      );
      newSelection.push(...tableData);

      onSelectionChange?.(newSelection);
    }
  }, [
    groupSelectionIds,
    isAllSelected,
    onSelectionChange,
    selection,
    tableData
  ]);

  const nameStyles = useMemo(
    () => ({
      gridColumnStart: showSelectionColumn ? 2 : 1,
      gridColumnEnd: `span ${
        showSelectionColumn ? columns.length - 1 : columns.length
      }`
    }),
    [columns.length, showSelectionColumn]
  );

  const backgroundColor = useMemo(() => {
    return header.colorBackground || "gray50";
  }, [header.colorBackground]);

  return (
    <WSElement className={styles.groupHeader} role="row">
      {showSelectionColumn && (
        <WSElement
          colorBackground={backgroundColor}
          className={styles.headerCell}
          role="cell"
        >
          {selectionType === "checkbox" ? (
            <WSControl
              type="multistep-checkbox"
              value={isAllSelected}
              onChange={handleSelectAll}
            />
          ) : null}
        </WSElement>
      )}

      <WSElement
        className={styles.headerCell}
        colorBackground={backgroundColor}
        style={nameStyles}
        role="cell"
      >
        <WSElement>
          <WSText.ParagraphSm weight="medium" color="gray500">
            {header.text}
          </WSText.ParagraphSm>
          {header.secondaryText && (
            <WSText.ParagraphXs color="gray400">
              {header.secondaryText}
            </WSText.ParagraphXs>
          )}
        </WSElement>
      </WSElement>
    </WSElement>
  );
};
